import { HttpClient } from '@angular/common/http';
import { Provider } from '@angular/core';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'packages/client/src/environments/environment';

const TRANSLATIONS_VERSION = environment.deployVersion ?? 'dev';

const translationFiles = [
    '',
    'abStats',
    'acl',
    'address-book',
    'anamnesis',
    'attendance',
    'basicSocialConsulting',
    'billing',
    'collection',
    'common',
    'confirmation',
    'contact',
    'contract',
    'dashboard',
    'delay-request',
    'diet',
    'donation',
    'donationReport',
    'donor',
    'enum',
    'emailTemplates',
    'ePrescriptions',
    'errors',
    'folder',
    'folderContact',
    'folderMedication',
    'form',
    'entities',
    'inbox',
    'individualPlan',
    'organization',
    'organization-settings',
    'person',
    'procedures',
    'providedOperationsReports',
    'service',
    'serviceType',
    'socialBilling',
    'socialCalendar',
    'socialFinancialDeposit',
    'socialPayment',
    'scheduler',
    'calendar',
    'summaryEmails',
    'system',
    'massEmails',
    'nationality',
    'table',
    'tools',
    'user',
    'warning',
    'workplace',
    'files',
    'workData',
    'workPlan',
    'validators',
    'voucher',
    'vzp',
    'sickNotes',
    'statistics',
    'serviceAnnouncement',
    'madoAccount',
    'madoCurrency',
    'madoJournal',
    'madoReport',
    'madoReconciliation',
    'madoCreditAgreement',
    'wysiwyg',
    'userTasks',
    'waitingList',
    'messages',
    'ePoukaz',
    'nursingAllowance',
    'socialCredits',
    'ereferrals',
];

function mergeObjectsRecursively(
    object: Record<string, unknown>,
    mergedObject: Record<string, unknown>,
): Record<string, unknown> {
    for (const key in object) {
        if (Object.prototype.hasOwnProperty.call(object, key)) {
            if (typeof object[key] === 'object' && object[key] !== null) {
                mergedObject[key] = mergeObjectsRecursively(
                    mergedObject[key] as Record<string, unknown>,
                    object[key] as Record<string, unknown>,
                );
            } else {
                mergedObject[key] = object[key];
            }
        }
    }

    return mergedObject;
}

class It2GoTranslateLoader extends TranslateLoader {
    constructor(private readonly http: HttpClient) {
        super();
    }

    private buildUrl(lang: string, key: string): string {
        if (key === '') {
            return `assets/i18n/${lang}.json`;
        } else {
            return `assets/i18n/${lang}/${key}.json`;
        }
    }

    getTranslation(lang: string): Observable<any> {
        return forkJoin(
            translationFiles.map((key) => {
                const url = this.buildUrl(lang, key);
                const version = TRANSLATIONS_VERSION;

                return this.http.get(url, {
                    params: { v: version },
                });
            }),
        ).pipe(
            map((responses: any) => {
                let mergedObject: Record<string, unknown> = {};
                let i = 0;

                for (const obj of responses) {
                    const rootKey = translationFiles[i++];
                    let o = obj as Record<string, unknown>;
                    if (rootKey != '') o = { [rootKey]: obj };

                    mergedObject = mergeObjectsRecursively(o, mergedObject);
                }

                return mergedObject;
            }),
        );
    }
}

function httpLoaderFactory(http: HttpClient): It2GoTranslateLoader {
    return new It2GoTranslateLoader(http);
}

export function provideIt2GoTranslateService(): Provider[] {
    return provideTranslateService({
        fallbackLang: 'cz',
        loader: {
            provide: TranslateLoader,
            useFactory: httpLoaderFactory,
            deps: [HttpClient],
        },
    });
}
